import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { CalendarCheck, Briefcase } from "@phosphor-icons/react";
import { PortalPage, Section, EmptyState, Pill } from "../../components/portal/PortalPage";
import { fmtDate, JOB_STATUS } from "../../components/portal/portalUtils";
import BookAppointment from "../../components/portal/BookAppointment";

/** Client portal — every open job that can still be booked or moved, with the slot picker on each. */
export default function ClientBookingPage() {
  const [jobs, setJobs] = useState(null);
  const load = useCallback(() => API.get("/client-hub/jobs?view=active").then(({ data }) => setJobs(data.jobs || [])).catch(() => setJobs([])), []);
  useEffect(() => { load(); }, [load]);
  const open = (jobs || []).filter((j) => !["completed", "cancelled"].includes(j.status) && !j.arrived_at);
  const unbooked = open.filter((j) => !j.scheduled_start);
  const booked = open.filter((j) => j.scheduled_start);
  const Row = ({ j }) => (
    <div className="flex flex-wrap items-center gap-3 py-3" data-testid={`booking-job-${j.id}`}>
      <div className="min-w-0 flex-1">
        <Link to={`/portal/jobs/${j.id}`} className="font-medium text-sm hover:text-primary" data-testid={`booking-job-link-${j.id}`}>{j.title}</Link>
        <p className="text-[11px] text-muted-foreground"><span className="font-mono">{j.reference}</span>{j.property_name ? ` · ${j.property_name}` : ""}</p>
      </div>
      <div className="text-xs text-right min-w-[120px]">
        {j.scheduled_start ? <><p className="font-semibold" data-testid={`booking-job-when-${j.id}`}>{fmtDate(j.scheduled_start)}</p>{j.schedule?.start_time && <p className="text-muted-foreground">{j.schedule.start_time}{j.schedule.end_time ? ` — ${j.schedule.end_time}` : ""}</p>}</> : <p className="text-muted-foreground">Not booked</p>}
      </div>
      <Pill map={JOB_STATUS} value={j.status} />
      <BookAppointment jobId={j.id} scheduled={!!j.scheduled_start} onBooked={load} />
    </div>
  );
  return (
    <PortalPage title="Book an appointment" subtitle="Pick a time that suits you for any open job. Slots show when one of our engineers is free." testid="client-booking-page">
      {jobs === null ? <div className="h-32 animate-pulse rounded-lg bg-muted/40" /> : open.length === 0 ? (
        <EmptyState icon={CalendarCheck} title="Nothing to book right now" text="When a job is raised for you it appears here so you can choose an appointment time." action={<Link to="/portal/requests/new" className="text-xs text-primary hover:underline" data-testid="booking-new-request-link">Raise a request</Link>} />
      ) : (
        <div className="space-y-6">
          <Section title={<span className="flex items-center gap-1.5"><CalendarCheck size={14} /> Waiting for a time ({unbooked.length})</span>} testid="booking-unbooked">
            {unbooked.length === 0 ? <p className="text-xs text-muted-foreground py-2">Every open job has an appointment.</p> : <div className="divide-y divide-border">{unbooked.map((j) => <Row key={j.id} j={j} />)}</div>}
          </Section>
          {booked.length > 0 && (
            <Section title={<span className="flex items-center gap-1.5"><Briefcase size={14} /> Booked ({booked.length})</span>} testid="booking-booked">
              <div className="divide-y divide-border">{booked.map((j) => <Row key={j.id} j={j} />)}</div>
            </Section>
          )}
        </div>
      )}
    </PortalPage>
  );
}
